/**
 * LEAD-CONTACT-FLOW-V1 -- the NEXT-card contact workflow's presentational
 * pieces. One card, one descending sequence, zero page change:
 *
 *   1. chooser (Phone primary / WhatsApp quiet) rides the NEXT band's
 *      actions row;
 *   2. the started workflow rides the band's composer strip: revealed
 *      number + the SERVER-issued grant countdown + the channel's HONEST
 *      SEAM line (text, never a control -- SEAM law);
 *   3. expiry or manual close re-masks IN PLACE (display act; the reveal
 *      is already audited -- no second row);
 *   4. the advance step ("How did the contact go?") hosts the EXISTING
 *      disposition panel (the banked config-backed vocabulary) -- no new
 *      writer, no invented enum.
 *
 * State lives in the caller (Lead360CanonicalView) via the pure model
 * (lead-contact-workflow-view.ts); everything here renders from props, so
 * the repo's react-dom/server static-render tests can pin every state.
 * The ONLY effectful piece is GrantCountdown's 1-second tick, which calls
 * onExpired exactly when the server-issued horizon passes -- the client
 * renders the countdown; it never extends it.
 */

import { useEffect, useState } from 'react';

import { PrimaryAction, QuietAction } from '@/dashboard/ui';
import { PHONE_MASK } from '@/dashboard/owner/LeadContactReveal';
import {
  channelSeamLine,
  CHOOSER_GROUP_LABEL,
  CHOOSER_PHONE_LABEL,
  CHOOSER_WHATSAPP_LABEL,
  CLOSE_AND_REMASK_LABEL,
  COUNTDOWN_PREFIX,
  formatGrantCountdown,
  grantRemainingSeconds,
  NO_VALUE_NEXT_LINE,
  NO_VALUE_REASON_COPY,
  REMASKED_NOTICE,
  REVEAL_AGAIN_LABEL,
  type ContactChannelChoice,
  type ContactWorkflowUiState,
} from '@/dashboard/leads/lead-contact-workflow-view';

import type { ReactNode } from 'react';

/** The channel chooser -- rides the NEXT band's actions row. ONE primary
 *  (Phone) + a quiet secondary (WhatsApp): one act, two channels (S-2). */
export function ContactWorkflowChooser({
  pending,
  onChoose,
}: {
  readonly pending: boolean;
  readonly onChoose: (channel: ContactChannelChoice) => void;
}): JSX.Element {
  return (
    <span
      className="lead-contact-workflow__chooser"
      aria-label={CHOOSER_GROUP_LABEL}
      data-testid="lead-contact-chooser"
    >
      <PrimaryAction
        type="button"
        data-testid="lead-contact-choose-phone"
        disabled={pending || undefined}
        onClick={(): void => onChoose('phone')}
      >
        {CHOOSER_PHONE_LABEL}
      </PrimaryAction>
      <QuietAction
        type="button"
        data-testid="lead-contact-choose-whatsapp"
        disabled={pending || undefined}
        onClick={(): void => onChoose('whatsapp')}
      >
        {CHOOSER_WHATSAPP_LABEL}
      </QuietAction>
    </span>
  );
}

/** The 1-second countdown against the SERVER-issued expiry. Calls
 *  onExpired once the horizon passes; renders the remaining time. The
 *  initial render (and every static render) derives from nowMs so the
 *  pinned tests are deterministic. */
export function GrantCountdown({
  expiresAtIso,
  onExpired,
  nowMs,
}: {
  readonly expiresAtIso: string;
  readonly onExpired: () => void;
  readonly nowMs?: number;
}): JSX.Element {
  const [tickNowMs, setTickNowMs] = useState<number>(nowMs ?? Date.now());
  const remaining = grantRemainingSeconds(expiresAtIso, tickNowMs);
  useEffect(() => {
    if (remaining <= 0) {
      onExpired();
      return undefined;
    }
    const timer = setInterval(() => {
      setTickNowMs(Date.now());
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining <= 0]);
  return (
    <span className="lead-contact-workflow__countdown" data-testid="lead-contact-countdown">
      {COUNTDOWN_PREFIX} {formatGrantCountdown(remaining)}
    </span>
  );
}

export interface ContactWorkflowPanelViewProps {
  readonly state: ContactWorkflowUiState;
  /** The EXISTING disposition panel (the advance step's one writer),
   *  injected by the host -- absent for callers without the action door. */
  readonly dispositionSlot?: ReactNode;
  /** Manual close -> the caller re-masks via the pure model (display act;
   *  no network, no second audit row). */
  readonly onCloseReveal?: () => void;
  /** Re-reveal after a re-mask -- a NEW audited act through the same ONE
   *  server door (each reveal writes its own audit row). */
  readonly onRevealAgain?: (channel: ContactChannelChoice) => void;
  /** Grant expiry -> the caller re-masks (the value is dropped). */
  readonly onGrantExpired?: () => void;
  /** Deterministic clock for static renders/tests. */
  readonly nowMs?: number;
}

/** The started workflow -- rides the NEXT band's composer strip. */
export function ContactWorkflowPanelView({
  state,
  dispositionSlot,
  onCloseReveal,
  onRevealAgain,
  onGrantExpired,
  nowMs,
}: ContactWorkflowPanelViewProps): JSX.Element | null {
  if (state.kind === 'idle') return null;

  if (state.kind === 'starting') {
    return (
      <div className="lead-contact-workflow" data-testid="lead-contact-workflow">
        <p className="lead360-muted" data-testid="lead-contact-starting">
          Contacting...
        </p>
      </div>
    );
  }

  if (state.kind === 'denied' || state.kind === 'disabled' || state.kind === 'failed') {
    return (
      <div className="lead-contact-workflow" data-testid="lead-contact-workflow">
        <p className="lead360-muted" data-testid={`lead-contact-${state.kind}`}>
          {state.message}
        </p>
      </div>
    );
  }

  // kind === 'active'
  const seam = channelSeamLine(state.channel);
  return (
    <div className="lead-contact-workflow" data-testid="lead-contact-workflow">
      {state.noValueReason !== null ? (
        <div data-testid="lead-contact-no-value">
          <p className="lead-contact-workflow__value lead-contact-workflow__value--empty">
            {NO_VALUE_REASON_COPY[state.noValueReason] ?? 'No phone to reveal'}
          </p>
          <p className="lead360-muted">{NO_VALUE_NEXT_LINE}</p>
        </div>
      ) : state.value !== null ? (
        <div data-testid="lead-contact-revealed">
          <p className="lead-contact-workflow__value" data-testid="lead-contact-value">
            {state.value}
          </p>
          {state.grant !== null ? (
            <GrantCountdown
              expiresAtIso={state.grant.expiresAtIso}
              onExpired={(): void => onGrantExpired?.()}
              {...(nowMs !== undefined ? { nowMs } : {})}
            />
          ) : null}
          <QuietAction
            type="button"
            size="sm"
            data-testid="lead-contact-close-reveal"
            onClick={(): void => onCloseReveal?.()}
          >
            {CLOSE_AND_REMASK_LABEL}
          </QuietAction>
          {/* THE HONEST SEAM (SEAM law): text only -- no handler, no href. */}
          <p className="lead360-muted lead-contact-workflow__seam" data-testid="lead-contact-seam">
            {seam}
          </p>
        </div>
      ) : (
        <div data-testid="lead-contact-remasked">
          <p className="lead-contact-workflow__value" data-testid="lead-contact-mask">
            {PHONE_MASK}
          </p>
          <p className="lead360-muted">{REMASKED_NOTICE}</p>
          <QuietAction
            type="button"
            size="sm"
            data-testid="lead-contact-reveal-again"
            onClick={(): void => onRevealAgain?.(state.channel)}
          >
            {REVEAL_AGAIN_LABEL}
          </QuietAction>
        </div>
      )}
      {/* The advance step: the EXISTING disposition panel carries the
          founder-worded question as ITS heading (one heading, DENSITY
          say-once) -- composed by the caller via the panel's heading prop. */}
      {state.noValueReason === null && dispositionSlot !== undefined ? (
        <div className="lead-contact-workflow__advance" data-testid="lead-contact-advance">
          {dispositionSlot}
        </div>
      ) : null}
    </div>
  );
}
